import React, { useEffect, useState, useLayoutEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as theme from "../../theme";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllPaymentOptionProvider,
  getPaymentOptionProviders,
} from "../../store/paymentprovider";
import { useLocation } from "react-router-dom";
import {
  getCovers,
  getInsurerDetails,
  getResponseDetails,
  getResponseDetailsForCompareAsArray,
  getSTCoptions,
  getSTCoptionsInsurerSelected,
  updateSTCOptionsToDB,
  updateSTCoptionRawQuoteInformation,
} from "../../store/quotes";
import {
  brokerFees,
  commissionRate,
  insurerFees,
  premium,
  premiumAndFees,
} from "../../store/businesslogic/hardwiredlogic";
import { DEFAULT_SELECTED_OPTION } from "../../store/businesslogic/hardwiredlogic";
import { getBrokerInformation } from "../../store/broker";

// Helper function for French-friendly labels
const getLocalizedLabel = (label, language = "en") => {
  if (!label) return "";
  if (typeof label === "string") return label; // Backward compatibility
  return label[language] || label.en || label;
};

const generateKeyFieldMapping = (ipfsObject) => {
  const mapping = {};
  for (const key in ipfsObject) {
    if (ipfsObject.hasOwnProperty(key)) {
      mapping[key] = key; // Assuming field names are the same as keys
    }
  }
  return mapping;
};
const PaymentOption = ({ prospectid, insurerId }) => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const selectedinsurer = useSelector(getSTCoptionsInsurerSelected);
  const [storeInsurerId, setStoreInsurerId] = useState(null);

  // Get broker information for language preference
  const { information } = useSelector(getBrokerInformation);
  const languagepreference = information?.languagepreference || "en";

  useEffect(() => {
    if (selectedinsurer && selectedinsurer.id) {
      const getSelectedInsurer = insurerId?.find(
        (s) => s.insurercode === selectedinsurer.id
      );
      setStoreInsurerId(getSelectedInsurer?._id || null);
    }
    if (selectedinsurer == DEFAULT_SELECTED_OPTION) {
      setShowForm(false);
    }
  }, [selectedinsurer]);

  const covers = useSelector(getCovers(storeInsurerId));
  const stcoptions = useSelector(getSTCoptions);

  const ipfs = stcoptions?.rawQuoteInformation?.rawQuoteInformation?.ipfs;

  useEffect(() => {
    if (covers !== null && storeInsurerId !== null) {
      const premiumValue =
        covers && parseInt(covers?.[premium()] || parseInt(ipfs?.["premium"]));
      const insurerFeeValue =
        covers &&
        parseFloat(
          covers?.[insurerFees()] || parseFloat(ipfs?.["insurerFees"])
        );
      const brokerFeeValue =
        parseInt(covers?.[brokerFees()]) ||
        parseFloat(ipfs?.["brokerFee"]) ||
        0;

      setFormState((prevState) => ({
        ...prevState,
        premium: premiumValue,
        insurerFees: insurerFeeValue,
        brokerFee: brokerFeeValue,
      }));
    }
  }, [storeInsurerId, covers]);

  const paymentProviders = useSelector(getPaymentOptionProviders);
  const keyFieldMapping = generateKeyFieldMapping(ipfs);

  useEffect(async () => {
    await dispatch(getAllPaymentOptionProvider());
  }, [stcoptions, dispatch]);

  const [showForm, setShowForm] = useState(false);
  const [isInitialised, setIsInitialised] = useState(true);
  const [selectedProviderObj, setSelectedProviderObject] = useState({});
  const [selectedProvider, setSelectedProvider] = useState({
    provider: null,
    keylist: [],
    formula: [],
    taxAmount: null,
    totalAmount: null,
  });

  const [formState, setFormState] = useState({
    premium: null,
    insurerFees: null,
    brokerFee: 0,
    taxRate: 0,
    taxAmount: null,
    totalAmount: null,
  });
  const [calculatedValues, setCalculatedValues] = useState({
    premium: 0,
    insurerFees: 0,
    brokerFee: 0,
    taxAmount: 0,
    totalAmount: 0,
  });

  useEffect(() => {
    if (isInitialised && ipfs) {
      const providerId =
        stcoptions?.rawQuoteInformation?.rawQuoteInformation?.paymentProviderId;

      if (ipfs) {
        const initialFormState = {};
        for (const key in keyFieldMapping) {
          initialFormState[keyFieldMapping[key]] = ipfs[key] || 0;
        }
        setFormState(initialFormState);
      }

      if (providerId && paymentProviders) {
        const selectedProvider = paymentProviders.find(
          (provider) => provider._id === providerId
        );
        setSelectedProviderObject(selectedProvider);

        if (selectedProvider) {
          setShowForm(true);
          setSelectedProvider(selectedProvider);
        }
      }

      setIsInitialised(false);
    }
  }, [isInitialised, ipfs, paymentProviders]);

  const handleDisplay = (keylist) => {
    return keylist.listValues.map((value) => ({
      label: value.name ? value.name : value,
      value: value.code ? value.code : value,
    }));
  };

  // A utility function to compare objects for equality

  const handleBlur = () => {
    updaterawQuoteInformation(formState);
  };
  const customStyles = {
    control: (provided) => ({
      ...provided,
      color: "red", // replace 'red' with your desired font color
      borderWidth: "2px",
      borderColor: " white ",
      "&:hover": {
        borderColor: " white",
      },
    }),
    option: (provided) => ({
      ...provided,
      color: "rgb(63, 68, 90)", // replace 'blue' with your desired font color for options
    }),
  };

  const dropdownOptions = paymentProviders.map((provider) => ({
    label: provider.provider,
    value: provider._id,
  }));

  useEffect(() => {
    if (
      selectedProvider &&
      selectedProvider.key === "gaCode" &&
      selectedProvider.listValues[0]
    ) {
      const defaultValue = {
        value: selectedProvider.listValues[0].code,
        label: selectedProvider.listValues[0].name,
      };
    }
  }, [selectedProvider]);

  useEffect(async () => {
    // Calculate premium value and update formState
    const taxRateValue = parseFloat(formState["taxRate"])
      ? parseFloat(formState["taxRate"])
      : 0;
    const premiumVal =
      parseFloat(formState?.["premium"]) || parseFloat(ipfs?.["premium"]);
    const insurerFeesVal =
      parseFloat(formState?.["insurerFees"]) ||
      parseFloat(ipfs?.["insurerFees"]) ||
      0;
    const brokerFee = parseFloat(formState?.["brokerFee"]);

    const taxAmount =
      (premiumVal + insurerFeesVal + brokerFee) * (taxRateValue / 100);
    const totalAmount = premiumVal + insurerFeesVal + brokerFee + taxAmount;

    // Update formState if values have changed
    if (
      taxAmount.toFixed(2) !== formState["taxAmount"] ||
      totalAmount.toFixed(2) !== formState["totalAmount"]
    ) {
      setFormState((prevState) => ({
        ...prevState,
        premium: premiumVal.toFixed(2),
        insurerFees: insurerFeesVal.toFixed(2),
        taxAmount: taxAmount.toFixed(2),
        totalAmount: totalAmount.toFixed(2),
      }));
    }
    if (ipfs && calculatedValues.brokerFee) {
      handleBlur();
    }
    // Update calculatedValues if brokerFee or taxRate changes
    if (
      brokerFee !== calculatedValues.brokerFee ||
      taxRateValue !== calculatedValues.taxRate
    ) {
      setCalculatedValues((prevValues) => ({
        ...prevValues,
        brokerFee,
        taxRate: taxRateValue,
        taxAmount,
        totalAmount,
      }));
    }
  }, [
    formState["premium"],
    formState["insurerFees"],
    formState["brokerFee"],
    formState["taxRate"],
    covers,
    calculatedValues.brokerFee,
    calculatedValues.taxRate,
    storeInsurerId,
  ]);

  const handleRateChange = (e, type) => {
    const value = parseFloat(e.target.value || 0);

    setFormState((prevState) => ({
      ...prevState,
      [type]: value,
    }));
  };

  const updaterawQuoteInformation = async (rawQuoteInformation) => {
    const data = {
      prospectId: prospectid,
      paymentProviderId: selectedProvider?._id,
      ipfs: rawQuoteInformation,
    };

    dispatch(
      updateSTCoptionRawQuoteInformation({
        key: "rawQuoteInformation",
        value: data,
      })
    );
  };

  const paymentProviderHandler = async (selectedOption) => {
    const selectedProvider = paymentProviders.find(
      (provider) => provider._id === selectedOption?.value
    );
    if (selectedProvider) setShowForm(true);
    // setFormState({});
    setSelectedProvider(selectedProvider);
    setSelectedProviderObject(selectedProvider);
  };

  const handleSelectChange = (selectedOption, keylist) => {
    const updatedFormState = {
      ...formState,
      [keylist.key]: selectedOption ? selectedOption.value : null,
    };
    setFormState(updatedFormState);
    updaterawQuoteInformation(updatedFormState);
  };

  const handleInputChange = (e, keylist) => {
    setFormState((prevState) => ({
      ...prevState,
      [keylist.key]: e.target.value,
    }));
  };

  const handleBindProviderValue = (formState, keylist) => {
    const listValueKey = formState[keylist.listValues[0]?.code];
    if (keylist.key === "gaCode" || keylist.key === "carrierCode") {
      const getProvider = keylist.listValues.find(
        (s) => s.code === formState[keylist.key]
      );
      return { value: getProvider?.code, label: getProvider?.name };
    }
    if (listValueKey) {
    } else if (formState[keylist.key]) {
      return {
        value: formState[keylist.key],
        label: formState[keylist.key],
      };
    } else if (keylist.key === "gaCode" && keylist.listValues[0]) {
      return {
        value: keylist.listValues[0].code,
        label: keylist.listValues[0].name,
      };
    } else {
      return { value: "", label: "" };
    }
  };

  const handleDateChange = async (date, keylist) => {
    const adjustedDate = new Date(date);
    adjustedDate.setDate(date.getDate());
    const formattedDate = formatTheDate(adjustedDate);
    await setFormState((prevState) => ({
      ...prevState,
      [keylist.key]: formattedDate,
    }));
  };

  const formatTheDate = (date) => {
    const formattedDate = new Date(date);
    const day = formattedDate.getDate();
    const month = formattedDate.getMonth() + 1; // Month is zero-based
    const year = formattedDate.getFullYear();

    return `${month}/${day}/${year}`;
  };

  const keylistDefaultValue = (keylist) =>
    selectedProvider.key === "gaCode"
      ? {
          value:
            selectedProvider.listValues[0]?.code || keylist.listValues[0]?.code,
          label:
            selectedProvider.listValues[0]?.name || keylist.listValues[0]?.name,
        }
      : null;

  return (
    <>
      <div style={{ width: "100%" }}>
        <div
          style={{
            minHeight: "7vh",
            paddingLeft: "9vmin",
            fontSize: "1.5vmin",
            paddingTop: "2vh",
          }}
        >
          <div className="form-group row ">
            <label className=" col-sm-3 col-form-label ">
              {languagepreference === "fr" ? "Sélectionner un partenaire de financement" : "Select financing partner"}
            </label>
            <div className="col-sm-8">
              <Select
                className="form-select"
                placeholder={languagepreference === "fr" ? "Sélectionner une option" : "Select an option"}
                styles={customStyles}
                onChange={paymentProviderHandler}
                options={dropdownOptions}
                style={{
                  height: "6vh",
                  fontSize: "1.5vmin",
                  border: "5px solid white",
                  borderRadius: 15,
                  fontFamily: "GibsonRegular",
                }}
                value={{
                  label: selectedProviderObj?.provider || "",
                  value: selectedProviderObj?._id || "",
                }}
              />
            </div>
          </div>
          {showForm &&
            selectedProvider.keylist.map((keylist) => (
              <div key={keylist.key} className="form-group row">
                <label className="col-sm-3 col-form-label">
                  {getLocalizedLabel(keylist.label, languagepreference)}
                </label>
                <div className="col-sm-8">
                  {keylist.keyType === "List" ? (
                    <Select
                      className="form-select"
                      placeholder={languagepreference === "fr" ? "Sélectionner une option" : "Select an option"}
                      styles={customStyles}
                      options={handleDisplay(keylist)}
                      onChange={(selectedOption) =>
                        handleSelectChange(selectedOption, keylist)
                      }
                      value={handleBindProviderValue(formState, keylist)}
                      defaultValue={keylistDefaultValue(keylist)}
                      isClearable={
                        keylist.key === "gaCode" &&
                        formState[keylist.key] !== null
                      }
                    />
                  ) : keylist.keyType === "Date" ? (
                    <DatePicker
                      selected={
                        formState[keylist.key]
                          ? new Date(formState[keylist.key])
                          : null
                      }
                      onChange={(date) => {
                        handleDateChange(date, keylist);
                      }}
                      onBlur={handleBlur}
                      dateFormat="MM/dd/yyyy" // Set the desired date format
                      className="form-control"
                      style={{
                        border: `2px solid ${theme.COLORS.white_Indo}`,
                        borderRadius: 5,
                        letterSpacing: 1.2,
                        backgroundColor: theme.COLORS.white_Indo,
                      }}
                    />
                  ) : (
                    <input
                      autoFocus
                      type={keylist.keyType.toLowerCase()}
                      className="form-control"
                      style={{
                        border: `2px solid ${theme.COLORS.white_Indo}`,
                        borderRadius: 5,
                        letterSpacing: 1.2,
                        backgroundColor: theme.COLORS.white_Indo,
                      }}
                      onChange={(e) => handleInputChange(e, keylist)}
                      onBlur={handleBlur}
                      value={formState[keylist.key]}
                    />
                  )}
                </div>
              </div>
            ))}

          {showForm &&
            selectedProvider.formula.map((data) => (
              <CommonInputField
                key={data.key}
                label={getLocalizedLabel(data.label, languagepreference)}
                type={data.keyType.toLowerCase()}
                name={data.key}
                onChange={(e) => handleRateChange(e, data.key)}
                value={formState[data.key] || 0}
                onBlur={handleBlur}
              />
            ))}
        </div>
      </div>
    </>
  );
};

const CommonInputField = ({
  label = "Sample common label",
  onChange,
  value,
  defaultValue,
  placeholder,
  type,
  onBlur,
  name,
}) => {
  const symbol = type === "percentage" ? "%" : "$";
  const isReadOnly = name === "taxAmount" || name === "totalAmount";

  return (
    <div className="form-group row">
      <label className="col-sm-3 col-form-label">{label}</label>
      <div className="col-sm-8">
        <div className="input-group">
          <input
            autoFocus
            type={type}
            name={name}
            className="form-control"
            placeholder={placeholder}
            style={{
              border: `2px solid ${theme.COLORS.white_Indo}`,
              borderRadius: 5,
              letterSpacing: 1.2,
              backgroundColor: theme.COLORS.white_Indo,
            }}
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            readOnly={isReadOnly}
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentOption;
